/**
 * MeasurementEquipmentFilters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 */
import React from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { InlineFilterBar, InlineSelect, InlineToggles } from '../../../components/common';
import type { InlineSelectOption } from '../../../components/common';
import { useFilterState } from '../../../hooks/useFilterState';
import {
  MeasurementEquipmentStatus,
  ValidityStatus,
  ValidityStatusLabels,
} from '../../../types/measurementEquipment';

export interface MeasurementFiltersData {
  searchTerm: string;
  companyId: string;
  occupationalRiskIds: number[];
  validityStatus: string;
  status: string;
  isGlobal: boolean;
}

interface OccupationalRiskOption {
  id: number;
  riskFactorName: string;
  riskType: string;
}

interface Props {
  onFilter: (filters: MeasurementFiltersData) => void;
  companies?: Array<{ id: number; name: string }>;
  risks?: OccupationalRiskOption[];
  loading?: boolean;
  total?: number;
}

export const MEASUREMENT_FILTERS_DEFAULT: MeasurementFiltersData = {
  searchTerm: '',
  companyId: '',
  occupationalRiskIds: [],
  validityStatus: '',
  status: '',
  isGlobal: false,
};

const defaultValues = MEASUREMENT_FILTERS_DEFAULT;

const VALIDITY_STATUS_OPTIONS: InlineSelectOption[] = Object.values(ValidityStatus).map((s) => ({
  value: s,
  label: ValidityStatusLabels[s],
}));

const STATUS_OPTIONS: InlineSelectOption[] = [
  { value: MeasurementEquipmentStatus.ACTIVE, label: 'Ativo' },
  { value: MeasurementEquipmentStatus.INACTIVE, label: 'Inativo' },
];

export const MeasurementEquipmentFiltersComponent: React.FC<Props> = ({
  onFilter,
  companies = [],
  risks = [],
  loading = false,
  total,
}) => {
  const getCompanyName = (id: string) => companies.find((c) => c.id === Number(id))?.name ?? id;

  const { filters, setFilter, clearAll, activeChips } = useFilterState(defaultValues, {
    onFilter,
    applyOnMount: true,
    live: true,
    labels: {
      companyId: 'Empresa',
      occupationalRiskIds: 'Riscos',
      validityStatus: 'Validade',
      status: 'Status',
    },
    formatValue: {
      companyId: (v: string) => getCompanyName(v),
      occupationalRiskIds: (v: number[]) => {
        if (!Array.isArray(v) || v.length === 0) return '';
        const names = v
          .map((id) => risks.find((r) => Number(r.id) === Number(id))?.riskFactorName)
          .filter((name): name is string => Boolean(name));
        return names.join(', ');
      },
      validityStatus: (v: string) => ValidityStatusLabels[v as ValidityStatus] ?? v,
      status: (v: string) => (v === MeasurementEquipmentStatus.ACTIVE ? 'Ativo' : 'Inativo'),
      isGlobal: () => '',
    },
  });

  const companyOptions: InlineSelectOption[] = companies.map((c) => ({
    value: String(c.id),
    label: c.name,
  }));

  const selectedRisks = risks.filter((r) => filters.occupationalRiskIds.includes(r.id));

  return (
    <InlineFilterBar
      searchPlaceholder="Buscar por nome, fabricante, modelo..."
      searchValue={filters.searchTerm}
      onSearchChange={(v) => setFilter('searchTerm', v)}
      resultCount={total}
      resultNoun="equipamentos"
      chips={activeChips}
      onClearAll={clearAll}
      loading={loading}
    >
      {companies.length > 0 && (
        <InlineSelect
          label="Empresa"
          value={filters.companyId}
          options={companyOptions}
          onChange={(v) => setFilter('companyId', v)}
          emptyLabel="Todas"
          minWidth={160}
        />
      )}

      {risks.length > 0 && (
        <Autocomplete<OccupationalRiskOption, true>
          multiple
          size="small"
          options={risks}
          getOptionLabel={(o) => o.riskFactorName}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={selectedRisks}
          onChange={(_, selected) => setFilter('occupationalRiskIds', selected.map((r) => r.id))}
          disabled={loading}
          sx={{ minWidth: 200 }}
          renderInput={(params) => <TextField {...params} label="Riscos Ocupacionais" />}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option.riskFactorName} size="small" {...getTagProps({ index })} key={option.id} />
            ))
          }
        />
      )}

      <InlineSelect
        label="Validade"
        value={filters.validityStatus}
        options={VALIDITY_STATUS_OPTIONS}
        onChange={(v) => setFilter('validityStatus', v)}
        emptyLabel="Todos"
        minWidth={140}
      />

      <InlineSelect
        label="Status"
        value={filters.status}
        options={STATUS_OPTIONS}
        onChange={(v) => setFilter('status', v)}
        emptyLabel="Todos"
        minWidth={130}
      />

      <InlineToggles
        disabled={loading}
        items={[
          {
            label: 'Apenas Globais',
            checked: filters.isGlobal,
            onChange: (v) => setFilter('isGlobal', v),
          },
        ]}
      />
    </InlineFilterBar>
  );
};

export default MeasurementEquipmentFiltersComponent;
